import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import {
  pixelBufferType,
  primitiveType,
  stencilAction,
  stencilFunc,
  stencilValue
} from "_src/engine/constants";

import {mat4, quat, vec3} from "_src/libs/gl-matrix";
import {screenUtils} from "_src/client/utils";
import {orthogonalization} from "_src/engine/math";
import LineShader from "_src/shaders/line/line";
import TextShader from "_src/shaders/text/text";
import DefaultShader from "_src/shaders/default/default_shader";
import appState from "_src/app_state";
import textWidget from "_src/client/widgets/text";
import {cadType, lineColorType, objectType} from "_src/constants";
import workspace from "_src/workspace";
import output from "_src/engine/output";
import loader from "_src/loader";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import dotLinePng from "_assets/textures/dot_line.png";

const quatTmp = quat.create();
const vec3Tmp = vec3.create();
const vec3Tmp1 = vec3.create();
const vec3Tmp2 = vec3.create();
const vec3Tmp3 = vec3.create();

let dottedLineTexture = null;

const COLOR = [0.0, 0.0, 0.0];
const END_SIZE = 20;
const LINE_WIDTH = 2;
const EXTENDED_PLANE_LINE_WIDTH = LINE_WIDTH * 2; //no aa in shader
const SEGMENT_LENGTH = 20;
const SELECTED_OPACITY = 1.0;
const BASE_OPACITY = 0.5;

export async function initMeasureLines() {
  if (dottedLineTexture) return;

  const image = await loader.load(dotLinePng, loader.IMAGE);
  if (!image) {
    output.error(output.DEVELOPERS_ERROR, "Couldn't process the extended plane texture.");
    return;
  }

  dottedLineTexture = bufferFactory.create(pixelBufferType.T_RGBA, {width: image.width, height: image.height, immutable: true, });
  dottedLineTexture.initMipMapLevelImage(0, image);
}

export function clearMeasureLines() {
  dottedLineTexture = null;
}

export default class MeasureLineWidget {
  constructor(lineObject) {
    this.hash = lineObject.getHash();
    this._lines = [];
    this._extendedPlane = new ExtendedPlane(lineObject);

    const isLinear = lineObject.type === objectType.LINEAR_MEASURE;
    if (isLinear) {
      if (lineObject.knots.length === 1) {
        const point = [...lineObject.knots[0].getData()];
        const measureWidget = new MeasureLine(point, isLinear);
        this._lines.push(measureWidget);
      } else {
        for (let i = 0; i < lineObject.knots.length - 1; i++) {
          const points = [...lineObject.knots[i].getData(), ...lineObject.knots[i+1].getData()];
          const measureWidget = new MeasureLine(points, isLinear);
          this._lines.push(measureWidget);
        }
      }
    } else {
      for (let i = 1; i < lineObject.knots.length; i++) {
        const knotData = lineObject.knots[i].getData();
        const measureWidget = new MeasureLine(knotData, isLinear);
        this._lines.push(measureWidget);
      }
    }
  }

  addToScenes(linesScene, textScene) {
    for (const line of this._lines) {
      line.addToScenes(linesScene, textScene);
    }
    this._extendedPlane.addToScene(linesScene);
  }

  removeFromScenes(linesScene, textScene) {
    for (const line of this._lines) {
      line.removeFromScenes(linesScene, textScene);
    }
    this._extendedPlane.removeFromScene(linesScene);
  }

  setVisibility(visible) {
    for (const line of this._lines) {
      line.setVisibility(visible);
    }
    this._extendedPlane.setVisibility(visible);
  }

  setSelection(selected, opaqueScene, transparentScene) {
    for (const line of this._lines) {
      line.setSelection(selected, opaqueScene, transparentScene);
    }
    this._extendedPlane.setSelection(selected, opaqueScene, transparentScene);
  }

  getPointModels() {
    const models = [];

    if (this._lines.length > 1) {
      const line = this._lines[0];
      if (line.endsModel) {
        const firstPointGroup = line.endsModel.getChildren()[0];
        const pointModel = firstPointGroup.getChildren()[0].getChildren()[0];
        models.push(pointModel);
      }
    }

    for (const line of this._lines) {
      if (!line.endsModel) continue;

      const secondPointGroup = line.endsModel.getChildren()[1];
      const pointModel = secondPointGroup.getChildren()[0].getChildren()[0];
      models.push(pointModel);
    }

    return models;
  }

  updateScreenAppearance(camera) {
    for (const line of this._lines) {
      line.updateScreenAppearance(camera);
    }
    this._extendedPlane.updateScreenAppearance(camera);
  }
}

class ExtendedPlane {
  constructor(lineObject) {
    const isLinear = lineObject.type === objectType.LINEAR_MEASURE;
    this.model = isLinear ? createExtendedPlane(lineObject.knots) : null;
  }

  addToScene(scene) {
    if (this.model) scene.add(this.model);
  }

  removeFromScene(scene) {
    if (this.model) scene.remove(this.model);
  }

  setVisibility(visible) {
    if (this.model) this.model.setVisibility(visible);
  }

  setSelection(selected, opaqueScene, transparentScene) {
    if (!this.model) return;

    const addScene = selected ? opaqueScene : transparentScene;
    const removeScene = selected ? transparentScene : opaqueScene;

    const opacity = selected ? SELECTED_OPACITY : BASE_OPACITY;
    this.model.setOpacity(opacity);

    addScene.add(this.model);
    removeScene.remove(this.model);
  }

  updateScreenAppearance(camera) {
    if (!this.model) return;

    const posBuffer = this.model.chunks[0].geometry.getItemBuffer("a_position");
    const posNextBuffer = this.model.chunks[0].geometry.getItemBuffer("a_position_next");
    let minTexturePeriod;
    for (let i=0; i<posBuffer.length/3; i+=2) {
      const pos1 = vec3.set(vec3Tmp, posBuffer[i*3], posBuffer[i*3+1], posBuffer[i*3+2]);
      const pos2 = vec3.set(vec3Tmp2, posNextBuffer[i*3], posNextBuffer[i*3+1], posNextBuffer[i*3+2]);
      const texturePeriod = screenUtils.calculateSegmentLinePeriod(pos1, pos2, SEGMENT_LENGTH, camera);
      minTexturePeriod = minTexturePeriod !== undefined ? Math.min(minTexturePeriod, texturePeriod) : texturePeriod;
    }
    this.model.shader.setTexturePeriod(minTexturePeriod);
  }
}

class MeasureLine {

  constructor(points, withArrows) {
    this._direction = vec3.create();
    this._right = vec3.create();
    this._up = vec3.create();
    this._origin = vec3.create();
    this.endsModel = null;
    this.lineModel = null;
    this.textModel = null;

    if (!points.length) return;

    const firstPoint = [points[0], points[1], points[2]];
    const lastPoint = [points[points.length-3], points[points.length-2], points[points.length-1]];

    if (points.length <= 3) {
      this.endsModel = this._createEnds(firstPoint, lastPoint, false);
    } else {
      this._initCoordinates(points);

      this.lineModel = this._createLine(points);
      this.textModel = this._createText(points);
      this.endsModel = this._createEnds(firstPoint, lastPoint, withArrows);
    }
  }

  updateScreenAppearance(camera) {
    if (this.endsModel) {
      const camDir = vec3.sub(vec3Tmp, camera.trans, this._origin);
      vec3.normalize(camDir, camDir);

      const camDirProjected = vec3.cross(vec3Tmp1, camDir, this._direction);
      vec3.cross(camDirProjected, this._direction, camDirProjected);

      const angle = Math.atan2(vec3.dot(vec3.cross(vec3Tmp2, camDirProjected, this._up), this._direction), vec3.dot(camDirProjected, this._up));
      const rotation = quat.setAxisAngle(quatTmp, this._direction, -angle);

      for (const group of this.endsModel.getChildren()) {
        const worldMatrix = group.modelMatrix;
        const worldPosition = [worldMatrix[12], worldMatrix[13], worldMatrix[14]];

        const scale = screenUtils.calculateObjectScale(END_SIZE, 4, worldPosition, camera);
        group.setScale(scale, scale, scale);
        group.copyRotation(rotation);
      }
    }

    if (this.textModel) {
      const cameraUp = vec3.set(vec3Tmp3, 0, 1, 0);
      vec3.transformQuat(cameraUp, cameraUp, camera.quat);
      const dot = vec3.dot(this._up, cameraUp);
      const flipText = dot < 0;
      textWidget.updateScreenAppearance(this.textModel, camera, flipText);
    }
  }

  setSelection(selected, opaqueScene, transparentScene) {
    const addScene = selected ? opaqueScene : transparentScene;
    const removeScene = selected ? transparentScene : opaqueScene;

    const opacity = selected ? SELECTED_OPACITY : BASE_OPACITY;
    const stencil = selected ? stencilValue.OPAQUE_LINES : stencilValue.TRANSPARENT_LINES;

    if (this.lineModel) {
      this.lineModel.setOpacity(opacity);
      this.lineModel.shader.setStencilReferenceValue(stencil);

      addScene.add(this.lineModel);
      removeScene.remove(this.lineModel);
    }

    if (this.textModel) {
      this.textModel.shader.setOutlineOpacity(opacity);
      for (const child of this.textModel.getChildren()) {
        child.setOpacity(opacity);
      }
    }

    if (this.endsModel) {
      this.endsModel.shader.setStencilReferenceValue(stencil);
      for (const endGroup of this.endsModel.getChildren()) {
        const endInnerGroup = endGroup.getChildren()[0];
        for (const endElement of endInnerGroup.getChildren()) {
          endElement.setOpacity(opacity);
        }
      }

      addScene.add(this.endsModel);
      removeScene.remove(this.endsModel);
    }
  }

  setVisibility(visible) {
    if (this.lineModel) this.lineModel.setVisibility(visible);
    if (this.textModel) textWidget.setVisibility(this.textModel, visible);

    if (this.endsModel) {
      for (const endGroup of this.endsModel.getChildren()) {
        const endInnerGroup = endGroup.getChildren()[0];
        for (const endElement of endInnerGroup.getChildren()) {
          endElement.setVisibility(visible);
        }
      }
    }
  }

  addToScenes(linesScene, textScene) {
    if (this.endsModel) linesScene.add(this.endsModel);
    if (this.lineModel) linesScene.add(this.lineModel);
    if (this.textModel) textScene.add(this.textModel);
  }

  removeFromScenes(linesScene, textScene) {
    if (this.endsModel) linesScene.remove(this.endsModel);
    if (this.lineModel) linesScene.remove(this.lineModel);
    if (this.textModel) textScene.remove(this.textModel);
  }

  _initCoordinates(points) {
    const firstPoint = [points[0], points[1], points[2]];
    const lastPoint = [points[points.length-3], points[points.length-2], points[points.length-1]];
    vec3.sub(this._direction, firstPoint, lastPoint);
    vec3.normalize(this._direction, this._direction);
    orthogonalization(this._direction, this._right, this._up);
    const midIdx = Math.floor(points.length / 3 / 2) * 3;

    if (points.length % 2 === 1) {
      this._origin = [points[midIdx], points[midIdx+1], points[midIdx+2]];
    } else {
      const mid1Point = [points[midIdx], points[midIdx+1], points[midIdx+2]];
      const mid2Point = [points[midIdx-3], points[midIdx-2], points[midIdx-1]];
      this._origin = vec3.add(vec3.create(), mid1Point, mid2Point);
      this._origin = vec3.scale(this._origin, this._origin, 0.5);
    }
  }

  _createText(points) {
    const textShader = new TextShader({
      depthTest: false,
      transparent: true,
    });

    let distanceSum = 0;
    for (let i=0; i<points.length-5; i+=3) {
      distanceSum += vec3.distance([points[i], points[i+1], points[i+2]], [points[i+3], points[i+4], points[i+5]]);
    }
    const lineText = distanceSum.toFixed(2) + " mm";
    const textModel = textWidget.create(lineText, textShader);

    const textRotation = quat.setAxes(quat.create(), this._up, this._right, this._direction);
    quat.conjugate(textRotation, textRotation);
    quat.rotateY(textRotation, textRotation, -0.5 * Math.PI);
    quat.rotateZ(textRotation, textRotation, -0.5 * Math.PI);

    const textLocalMat = mat4.fromRotationTranslation(mat4.create(), textRotation, this._origin);
    textModel.setMatrix(textLocalMat);
    textModel.updateMatrix();

    return textModel;
  }

  _createLine(points) {
    const lineShader = new LineShader({
      antialiasing: false,
      lineWidth : LINE_WIDTH,
      transparent : true,
      backFaceCulling: false,
      screenSpace : false
    });
    lineShader.enableStencilTest();
    lineShader.setStencilFrontZPassAction(stencilAction.REPLACE);
    lineShader.setStencilBackZPassAction(stencilAction.REPLACE);
    lineShader.setStencilFrontZFailAction(stencilAction.REPLACE);
    lineShader.setStencilBackZFailAction(stencilAction.REPLACE);
    lineShader.setStencilFunction(stencilFunc.ALWAYS);

    const lineModel = objFactory.createDrawable(lineShader);
    lineModel.setDiffuseColor(...COLOR);

    const primitives = [];
    for (let i = 1; i < points.length / 3; ++i) {
      primitives.push(i - 1, i);
    }

    lineModel.addGeometryChunk(primitiveFactory.create(primitiveType.LINE_ARRAY, { points, primitives }));
    lineModel.chunks[0].enableFrustumTest = false;
    lineModel.updateMatrix();

    return lineModel;
  }

  _createEnds(firstPoint, lastPoint, withArrow) {
    const shader = new DefaultShader({
      transparent: true,
      backFaceCulling: false,
      lighting: false,
      writeObjectID: false,
      backFaceMode: appState.BM_FRONTFACE
    });
    shader.enableStencilTest();
    shader.setStencilFrontZPassAction(stencilAction.REPLACE);
    shader.setStencilBackZPassAction(stencilAction.REPLACE);
    shader.setStencilFrontZFailAction(stencilAction.REPLACE);
    shader.setStencilBackZFailAction(stencilAction.REPLACE);
    shader.setStencilFunction(stencilFunc.ALWAYS);

    const wholeModel = objFactory.createDrawable(shader);
    const groupModel1 = objFactory.createDrawable(shader);
    const groupModel2 = objFactory.createDrawable(shader);
    const groupModelInner1 = objFactory.createDrawable(shader);
    const groupModelInner2 = objFactory.createDrawable(shader);

    groupModel1.add(groupModelInner1);
    groupModel2.add(groupModelInner2);
    wholeModel.add(groupModel1);
    wholeModel.add(groupModel2);

    const sphereModel1 = createSphere(shader);
    const sphereModel2 = createSphere(shader);
    groupModelInner1.add(sphereModel1);
    groupModelInner2.add(sphereModel2);

    const rotation = quat.setAxes(quat.create(), this._up, this._right, this._direction);
    quat.conjugate(rotation, rotation);

    const backRotation = quat.setAxes(quat.create(), this._up, vec3.negate(vec3.create(), this._right), vec3.negate(vec3.create(), this._direction));
    quat.conjugate(backRotation, backRotation);

    if (withArrow) {
      const arrowModel1 = createArrow(shader);
      const arrowModel2 = createArrow(shader);
      groupModelInner1.add(arrowModel1);
      groupModelInner2.add(arrowModel2);

      arrowModel1.setTranslation(0,1,0);
      arrowModel2.setTranslation(0,1,0);
    }

    groupModel1.setTranslation(...firstPoint);
    groupModel2.setTranslation(...lastPoint);
    groupModelInner1.copyRotation(backRotation);
    groupModelInner2.copyRotation(rotation);

    wholeModel.updateMatrix();

    return wholeModel;
  }
}

function createArrow(shader) {
  const drawable = objFactory.createDrawable(shader);
  const geometry = primitiveFactory.create(primitiveType.ARROW, {});
  drawable.addGeometryChunk(geometry);

  drawable.setDiffuseColor(...COLOR);
  drawable.setFrustrumCulling(false);

  return drawable;
}

function createSphere(shader) {
  const sphereGeometry = primitiveFactory.create(primitiveType.SPHERE);
  const sphereModel = objFactory.createDrawable(shader);
  sphereModel.addGeometryChunk(sphereGeometry);
  sphereModel.setDiffuseColor(...COLOR);

  return sphereModel;
}

function createExtendedPlaneModel(points) {
  const lineShader = new LineShader({
    antialiasing: false,
    lineWidth : EXTENDED_PLANE_LINE_WIDTH,
    transparent : false,
    backFaceCulling: false,
    colorType : lineColorType.TEXTURE,
    screenSpace : false
  });

  const lineModel = objFactory.createDrawable(lineShader);
  lineModel.setTexture(dottedLineTexture, 0);

  const primitives = [];
  for (let i = 0; i < points.length / 3; ++i) {
    primitives.push(i);
  }

  lineModel.addGeometryChunk(primitiveFactory.create(primitiveType.LINE_ARRAY, { points, primitives }));
  lineModel.chunks[0].enableFrustumTest = false;
  lineModel.updateMatrix();

  return lineModel;
}

function createExtendedPlane(knots) {
  const planeToPoints = new Map();
  for (const knot of knots) {
    if (!knot.isPerpendicular) continue;
    const cadObject = workspace.getObjectByUUID(knot.parentUUID);
    if (!cadObject || cadObject.getSubType() !== cadType.PLANE) {
      output.warn("Perpendicular linear measure's referenced object is not a plane");
      continue;
    }

    const toLocalMat = mat4.invert(mat4.create(), mat4.mul(mat4.create(), cadObject.model.modelMatrix, cadObject.getModelData()["transform"]));
    const pointOnPlane = vec3.transformMat4(vec3.create(), knot.getData(), toLocalMat);

    if (!planeToPoints.has(cadObject)) {
      planeToPoints.set(cadObject, []);
    }

    const planePoints = planeToPoints.get(cadObject);
    planePoints.push(pointOnPlane);
  }

  const planeLines = [];
  const planeHalfSize = 0.5;

  for (const [plane, points] of planeToPoints.entries()) {
    let xMin = planeHalfSize;
    let yMin = planeHalfSize;
    let xMax = -planeHalfSize;
    let yMax = -planeHalfSize;

    for (const point of points) {
      xMin = Math.min(xMin, point[0]);
      yMin = Math.min(yMin, point[1]);
      xMax = Math.max(xMax, point[0]);
      yMax = Math.max(yMax, point[1]);
    }

    const leftExtended = xMin < -planeHalfSize;
    const rightExtended = xMax > planeHalfSize;
    const bottomExtended = yMin < -planeHalfSize;
    const topExtended = yMax > planeHalfSize;

    if (topExtended) {
      createExtendedPart(leftExtended, rightExtended, xMin, xMax, yMax, planeHalfSize,
        (x1, y1, x2, y2) => {
          const fromLocalMat = mat4.mul(mat4.create(), plane.model.modelMatrix, plane.getModelData()["transform"]);
          const worldSpaceCoords1 = vec3.transformMat4(vec3.create(), vec3.fromValues(x1, y1, 0), fromLocalMat);
          const worldSpaceCoords2 = vec3.transformMat4(vec3.create(), vec3.fromValues(x2, y2, 0), fromLocalMat);
          planeLines.push(worldSpaceCoords1[0], worldSpaceCoords1[1], worldSpaceCoords1[2], worldSpaceCoords2[0], worldSpaceCoords2[1], worldSpaceCoords2[2]);
        });
    }
    if (rightExtended) {
      createExtendedPart(topExtended, bottomExtended, -yMax, -yMin, xMax, planeHalfSize,
        (x1, y1, x2, y2) => {
          const fromLocalMat = mat4.mul(mat4.create(), plane.model.modelMatrix, plane.getModelData()["transform"]);
          const worldSpaceCoords1 = vec3.transformMat4(vec3.create(), vec3.fromValues(y1, -x1, 0), fromLocalMat);
          const worldSpaceCoords2 = vec3.transformMat4(vec3.create(), vec3.fromValues(y2, -x2, 0), fromLocalMat);
          planeLines.push(worldSpaceCoords1[0], worldSpaceCoords1[1], worldSpaceCoords1[2], worldSpaceCoords2[0], worldSpaceCoords2[1], worldSpaceCoords2[2]);
        });
    }

    if (bottomExtended) {
      createExtendedPart(rightExtended, leftExtended, -xMax, -xMin, -yMin, planeHalfSize,
        (x1, y1, x2, y2) => {
          const fromLocalMat = mat4.mul(mat4.create(), plane.model.modelMatrix, plane.getModelData()["transform"]);
          const worldSpaceCoords1 = vec3.transformMat4(vec3.create(), vec3.fromValues(-x1, -y1, 0), fromLocalMat);
          const worldSpaceCoords2 = vec3.transformMat4(vec3.create(), vec3.fromValues(-x2, -y2, 0), fromLocalMat);
          planeLines.push(worldSpaceCoords1[0], worldSpaceCoords1[1], worldSpaceCoords1[2], worldSpaceCoords2[0], worldSpaceCoords2[1], worldSpaceCoords2[2]);
        });
    }

    if (leftExtended) {
      createExtendedPart(bottomExtended, topExtended, yMin, yMax, -xMin, planeHalfSize,
        (x1, y1, x2, y2) => {
          const fromLocalMat = mat4.mul(mat4.create(), plane.model.modelMatrix, plane.getModelData()["transform"]);
          const worldSpaceCoords1 = vec3.transformMat4(vec3.create(), vec3.fromValues(-y1, x1, 0), fromLocalMat);
          const worldSpaceCoords2 = vec3.transformMat4(vec3.create(), vec3.fromValues(-y2, x2, 0), fromLocalMat);
          planeLines.push(worldSpaceCoords1[0], worldSpaceCoords1[1], worldSpaceCoords1[2], worldSpaceCoords2[0], worldSpaceCoords2[1], worldSpaceCoords2[2]);
        });
    }
  }

  const model = planeLines.length ? createExtendedPlaneModel(planeLines) : null;

  return model;
}

function createExtendedPart(leftExtended, rightExtended, xMin, xMax, yMax, planeHalfSize, addLinePointsFunction) {
  const leftPoint = [0, yMax];
  if (leftExtended) {
    leftPoint[0] = xMin;
  } else {
    addLinePointsFunction(-planeHalfSize, planeHalfSize, -planeHalfSize, yMax);
    leftPoint[0] = -planeHalfSize;
  }

  const rightPoint = [0, yMax];
  if (rightExtended) {
    rightPoint[0] = xMax;
  } else {
    addLinePointsFunction(planeHalfSize, planeHalfSize, planeHalfSize, yMax);
    rightPoint[0] = planeHalfSize;
  }

  addLinePointsFunction(leftPoint[0], leftPoint[1], rightPoint[0], rightPoint[1]);
}
